import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { listarNotificacoes, marcarNotificacaoLida, marcarTodasNotificacoesLidas } from "../services/api";
import { useAuth } from "../context/AuthContext";
import NovidadesModal from "./NovidadesModal";
import {
  novidadesPara, publicoDoUsuario, novidadesVistas, marcarNovidadeVista,
  novidadesAutoAbertas, marcarAutoAberta,
} from "../data/novidades";

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [itens, setItens] = useState([]);
  const [carregou, setCarregou] = useState(false);
  const [aberto, setAberto] = useState(false);
  const caixa = useRef(null);

  // ✨ Novidades do portal (05/11/2026). Ficam no topo do sino, contam no número e abrem num modal.
  const novidades = novidadesPara(publicoDoUsuario(user));
  const [vistas, setVistas] = useState(() => novidadesVistas(user));
  const [modal, setModal] = useState(null); // { indice, auto }
  const [balao, setBalao] = useState(false);

  // 18/10/2026 (Nelson): clicar fora do popup (ou Esc) fecha — antes só fechava clicando no sino de novo
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (ev) => { if (caixa.current && !caixa.current.contains(ev.target)) setAberto(false); };
    const esc = (ev) => { if (ev.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", fora);
    document.addEventListener("touchstart", fora);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); document.removeEventListener("touchstart", fora); document.removeEventListener("keydown", esc); };
  }, [aberto]);

  const carregar = useCallback(async () => {
    try { setItens(await listarNotificacoes()); } catch { /* sem login ou offline */ }
    setCarregou(true);
  }, []);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 30000); // poll a cada 30s
    return () => clearInterval(t);
  }, [carregar]);

  useEffect(() => { setVistas(novidadesVistas(user)); }, [user]);

  const avisosNaoLidos = itens.filter((n) => !n.lida).length;
  const novidadesNaoVistas = novidades.filter((n) => !vistas.has(n.id)).length;
  const naoLidas = avisosNaoLidos + novidadesNaoVistas;

  // Abre SOZINHO uma vez por novidade (a 1ª ainda não vista e que nunca se apresentou), sem marcar como vista:
  // fechou no ✕, continua "novo" no sino. Nelson, 05/11: "abre uma vez sozinho mas não marca como lido".
  useEffect(() => {
    if (!user || !novidades.length) return;
    const auto = novidadesAutoAbertas(user);
    const i = novidades.findIndex((n) => !vistas.has(n.id) && !auto.has(n.id));
    if (i < 0) return;
    const t = setTimeout(() => {
      marcarAutoAberta(user, novidades.filter((n) => !vistas.has(n.id)).map((n) => n.id));
      setModal({ indice: i, auto: true });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 💬 BALÃO "Tem novidades na sua caixa" (Nelson, 05/11: "nem sempre a pessoa vê o vermelho no sininho").
  // Uma vez por visita (sessão), só com algo não lido, nunca por cima do modal, e some sozinho em 7 s.
  useEffect(() => {
    if (!carregou || modal || aberto || naoLidas === 0) return undefined;
    let jaMostrou = false;
    try { jaMostrou = sessionStorage.getItem("eairh_balao_sino") === "1"; } catch { /* sem storage */ }
    if (jaMostrou) return undefined;
    const t = setTimeout(() => {
      setBalao(true);
      try { sessionStorage.setItem("eairh_balao_sino", "1"); } catch { /* sem storage */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [carregou, modal, aberto, naoLidas]);
  useEffect(() => {
    if (!balao) return undefined;
    const t = setTimeout(() => setBalao(false), 7000);
    return () => clearTimeout(t);
  }, [balao]);

  const textoBalao = () => {
    const partes = [];
    if (novidadesNaoVistas) partes.push(`${novidadesNaoVistas} ${novidadesNaoVistas === 1 ? "novidade" : "novidades"}`);
    if (avisosNaoLidos) partes.push(`${avisosNaoLidos} ${avisosNaoLidos === 1 ? "aviso novo" : "avisos novos"}`);
    return `Você tem ${partes.join(" e ")} 👀`;
  };

  const abrir = async (n) => {
    if (!n.lida) { try { await marcarNotificacaoLida(n.id); carregar(); } catch {} }
    // Notificação com link → navega (ex.: alerta de prazo abre o painel apontando pro relato).
    if (n.link) { setAberto(false); navigate(n.link); }
  };

  // 18/10/2026 (Nelson: "nem sempre a pessoa quer abrir o que está na notificação"): marca como lida sem abrir.
  const soMarcar = async (ev, n) => {
    ev.stopPropagation();
    setItens((v) => v.map((x) => (x.id === n.id ? { ...x, lida: true } : x)));
    try { await marcarNotificacaoLida(n.id); } catch {}
  };

  const marcarTodas = async () => {
    try { await marcarTodasNotificacoesLidas(); carregar(); } catch {}
  };

  const ver = (id) => { marcarNovidadeVista(user, id); setVistas(novidadesVistas(user)); };
  const abrirNovidade = (indice) => { setAberto(false); setBalao(false); ver(novidades[indice].id); setModal({ indice, auto: false }); };
  const trocarIndice = (indice) => {
    if (indice < 0 || indice >= novidades.length) return;
    if (!modal?.auto) ver(novidades[indice].id);
    setModal((m) => ({ ...m, indice }));
  };
  const acao = (n) => { ver(n.id); setModal(null); navigate(n.link); };

  return (
    <div className="relative" ref={caixa}>
      <button
        onClick={() => { setBalao(false); setAberto((v) => !v); }}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/15"
        aria-label={naoLidas > 0 ? `Notificações (${naoLidas} não lidas)` : "Notificações"}
        aria-haspopup="true"
        aria-expanded={aberto}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.7 21a2 2 0 01-3.4 0" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {naoLidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-orange-500 text-[10px] font-bold flex items-center justify-center">
            {naoLidas}
          </span>
        )}
      </button>

      {balao && !aberto && (
        <button onClick={() => { setBalao(false); setAberto(true); }}
          className="absolute right-0 top-11 z-50 w-56 rounded-xl bg-white px-3 py-2.5 text-left text-sm font-semibold text-gray-800 shadow-xl border border-orange-200">
          <span className="absolute -top-1.5 right-3 h-3 w-3 rotate-45 border-l border-t border-orange-200 bg-white" />
          {textoBalao()}
          <span className="block text-xs font-normal text-gray-500 mt-0.5">Toque no sino para ver</span>
        </button>
      )}

      {aberto && (
        <div className="absolute right-0 mt-2 w-80 max-h-[28rem] flex flex-col rounded-xl bg-white text-gray-800 shadow-xl border border-gray-200 z-50">
          <div className="px-4 py-3 border-b">
            <span className="font-semibold text-sm">Notificações</span>
          </div>
          <div className="flex-1 overflow-auto">
            {novidades.length > 0 && (
              <div className="border-b">
                <p className="px-4 pt-3 pb-1 text-[11px] font-bold tracking-wide text-orange-500">✨ NOVIDADES</p>
                {novidades.map((n, i) => {
                  const nova = !vistas.has(n.id);
                  return (
                    <button key={n.id} onClick={() => abrirNovidade(i)}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-gray-50">
                      <span className="text-base">{n.ic}</span>
                      <span className={`flex-1 text-sm ${nova ? "font-semibold" : "text-gray-500"}`}>{n.titulo}</span>
                      {nova && <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white">novo</span>}
                    </button>
                  );
                })}
                <Link to="/novidades" onClick={() => setAberto(false)}
                  className="block px-4 pb-2.5 pt-1 text-right text-xs font-semibold text-primary hover:underline">Ver todas as novidades ›</Link>
              </div>
            )}
            {itens.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">Sem notificações.</div>
            ) : (
              itens.map((n) => (
                <div key={n.id} className={`group flex items-start gap-2 border-b last:border-0 hover:bg-gray-50 ${n.lida ? "opacity-60" : ""}`}>
                  <button onClick={() => abrir(n)} className="flex-1 min-w-0 text-left px-4 py-3">
                    <div className="flex items-start gap-2">
                      {!n.lida && <span className="mt-1.5 h-2 w-2 rounded-full bg-orange-500 shrink-0" />}
                      <div>
                        <p className="text-sm">{n.texto}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(n.criadoEm).toLocaleString("pt-BR")}</p>
                      </div>
                    </div>
                  </button>
                  {!n.lida && (
                    <button onClick={(ev) => soMarcar(ev, n)} title="Marcar como lida (sem abrir)"
                      className="shrink-0 mt-2.5 mr-2 rounded-full px-2 py-1 text-[11px] font-semibold text-gray-500 hover:bg-gray-200 hover:text-gray-800">✓ lida</button>
                  )}
                </div>
              ))
            )}
          </div>
          {/* rodapé fixo do popup (não no fim da lista): sempre à mão, mesmo com muitas notificações */}
          {itens.length > 0 && (
            <div className="border-t px-4 py-2.5 text-center">
              {avisosNaoLidos > 0
                ? <button onClick={marcarTodas} className="text-xs text-primary font-semibold hover:underline">Marcar todas como lidas ({avisosNaoLidos})</button>
                : <span className="text-xs text-gray-400">Tudo lido ✓</span>}
            </div>
          )}
        </div>
      )}

      {modal && (
        <NovidadesModal lista={novidades} indice={modal.indice} onIndice={trocarIndice}
          onFechar={() => setModal(null)} onAcao={acao} />
      )}
    </div>
  );
}
